import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { Unsubscribe } from 'router5/dist/types/base';
import { IntlShape } from 'react-intl';
import { IAgentMemoryDTO, IEventDetailsDTO, IEventsDAO, IOnSyncEventSubscriptionResponse } from '../../../api/events.dao';
import { IRouter } from '../../../infrastructure/router';
import { IAppsyncSubscription, subscribeAppsyncData } from '../../../infrastructure/utils/appsync.utils';
import { ISubWindowFacade, ISubWindowsService, SubWindowStatus } from '../../../infrastructure/sub-windows';
import { Routes } from '../../../routes-table';
import { formatTimezoneOffset, getTimezoneOffsetInMinutes } from '../../../infrastructure/utils/date-fns-overrides.utils';
import { getFormattedDatesString } from '../../../infrastructure/utils/date.util';
import { EventForm } from '../_events-list/_event-form/event-form.container';
import { timezonesCodeLabelMap } from '../../../infrastructure/timezones-code-label-map';
import { IConnectionDetailsDTO, IDeleteConnectionRequestParams } from '../../../api/connections.dao';
import { IAttendeeDetailsDTO } from '../../../api/attendees.dao';
import { alertServerError, closeEventNotifications } from '../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../infrastructure/error-parsing';
import { MeetingsListViewModel } from './_meetings-list/meetings-list.viewmodel';
import { RoomsViewModel } from './_rooms/rooms.viewmodel';
import { ConnectionsViewModel } from './_connections/connections.viewmodel';
import { OrganizationsListViewModel } from './_organizations-list/organizations-list.viewmodel';
import { AttendeeListViewModel } from './_attendees-list/attendees-list.viewmodel';
import { EditWebsiteViewModel } from './_edit-website/edit-website.viewmodel';
import ControlViewModel from './_control/control.viewmodel';
import { SalesforceOrderViewModel } from './shared/salesforce-order/salesforce-order.viewmodel';
import { GuidelinesForm } from './_control/_guidelines-form/guidelines-form.container';
import { AgentMemoryForm } from './_control/_agent-memory-form/agent-memory-form.container';
import { EDIT_AGENT_MEMORY_POPUP_TITLE_TEXT } from './_control/_agent-memory-form/agent-memory-form.text';
import { WebParticipantsModalHostProps } from './_connections/_meeting-connections-form/unique-joining-link-modal/unique-joining-link-modal.viewmodel';
import ReportsViewModel from './_reports/reports.viewmodel';
import TrackingServiceProvider from '../../../tracking-service/tracking-service-provider';
import { onSyncEvent } from '../../../graphql/subscriptions';
import {
    ARTIFACTS_PAGE_TRACKING_EVENTS,
    CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS,
    convertBoolTypeForMixPanel,
    OpenFrom,
    TRACKING_EVENTS
} from '../../../tracking-service/tracking-values';
import { IGlobalStorage } from '../../../infrastructure/global-storage';
import AssetsViewModel from './_assets/assets.viewmodel';
import ArtifactsViewModel from './_artifacts/artifacts.viewmodel';
import TroubleshootModalViewModel from './shared/troubleshoot-modal/troubleshoot-modal.viewmodel';
import { IOrganizationsForSpeakersSync } from './shared/connections-list/_connection-form/_sync-speakers/sync-speakers.viewmodel';
import { IDeleteConnectionDetails } from './shared/connections-list/_connection-delete-confirm-popover';
import { ConfigurePassportDisplayForm } from './shared/configure-passport-display/configure-passport-display.container';
import { IConfigurePassportDisplayFormProps } from './shared/configure-passport-display/configure-passport-display.viewmodel';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { CONFIGURE_PASSPORT_DISPLAY_TEXT, ERROR_TEXT } from '../../../constants/common-labels.constants';
import { PassportConfigurationMode } from './shared/configure-passport-display/configure-passport-display.config';
import { transpileClassesToInlineStyles, transpileInlineStylesToClasses } from '../../global/rich-text-editor/utils/transpiler';
import { IEventRequestParams } from '../../../api/shared.dto';

enum ManageEventPages {
    Schedule,
    Organizations,
    Attendees,
    Website,
    Assets,
    Artifacts,
    Rooms,
    Control,
    Connections,
    Reports
}

enum GuidelinesMode {
    Edit = 'edit',
    Create = 'create',
}

interface IEditMeetingConnectionDetailsProps {
    organizationsForSpeakersSync: IOrganizationsForSpeakersSync;
}


export interface ISubWindowFormMethods {
    [x: string]: {
        openEditSubWindow: (id: number, ...args) => void;
    };
    attendees?: {
        openEditSubWindow: (id: number, openFrom: OpenFrom, callback?: (attendee: IAttendeeDetailsDTO) => void) => void;
        openNewSubWindow?: (openFrom: OpenFrom, callback?: (attendee: IAttendeeDetailsDTO) => void) => void;
    };
    meetingConnectionDetails?: {
        openEditSubWindow: (
            id: number,
            connection: IConnectionDetailsDTO,
            callback?: () => void,
            shouldSetModalCallback?: boolean,
            shouldShowKnovioReplayToggle?: boolean,
            props?: IEditMeetingConnectionDetailsProps
        ) => void;
        openDeleteSubWindow?: (id: number, data: IDeleteConnectionRequestParams, callback?: () => void, connectionDetails?: IDeleteConnectionDetails) => void;
        openWebRegistrantsModal?: (id: number, componentToHostProps: WebParticipantsModalHostProps) => void;
        closeWebRegistrantsModal?: (id: number) => void;
    };
    roomConnectionDetails?: {
        openEditSubWindow: (id: number, connection: IConnectionDetailsDTO, callback?: () => void, shouldSetModalCallback?: boolean) => void;
        openDeleteSubWindow?: (id: number, data: IDeleteConnectionRequestParams, callback?: () => void, connectionDetails?: IDeleteConnectionDetails) => void;
    };
}

class ManageEventViewModel {
    @mobx.observable
    private event: IEventDetailsDTO;

    @mobx.observable
    private page: ManageEventPages;

    @mobx.observable
    private loadingEvent: boolean;

    @mobx.observable
    private editEventForm: ISubWindowFacade;

    private subscriptions: Unsubscribe[];

    private eventSubscriptions: IAppsyncSubscription[];

    private meetingsListViewModel: MeetingsListViewModel;

    private roomsViewModel: RoomsViewModel;

    private connectionsViewModel: ConnectionsViewModel;

    private editWebsiteVM: EditWebsiteViewModel;

    private assetsVM: AssetsViewModel;

    private artifactsVM: ArtifactsViewModel;

    private organizationsListViewModel: OrganizationsListViewModel;

    private attendeesListViewModel: AttendeeListViewModel;

    private controlVM: ControlViewModel;

    private salesforceOrderVM: SalesforceOrderViewModel;

    private reportsVM: ReportsViewModel;

    @mobx.observable
    private attendeeIdToEditPopupMap: { [key: string]: ISubWindowFacade };

    @mobx.observable
    private guidelinesFormSubWindow: { [key: string]: ISubWindowFacade };

    private agentMemoryFormSubWindow: ISubWindowFacade;
    private loadedAgentMemory = '';

    @mobx.observable
    private configurePassportDisplayFormSubWindow: { [key: string]: ISubWindowFacade };

    private subWindowFormMethods: ISubWindowFormMethods = {};

    @mobx.observable
    private eventNotes: string;
    private troubleshootVM: TroubleshootModalViewModel;

    constructor(
        private readonly eventId: number,
        private readonly accountId: number,
        public readonly intl: IntlShape,
        @IoC.Inject private readonly eventsDAO = IoC.Container.get(IEventsDAO),
        @IoC.Inject private readonly router = IoC.Container.get(IRouter),
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {
        this.meetingsListViewModel = null;
        this.organizationsListViewModel = null;
        this.attendeesListViewModel = null;
        this.roomsViewModel = null;
        this.connectionsViewModel = null;
        this.editWebsiteVM = null;
        this.controlVM = null;
        this.salesforceOrderVM = null;
        this.reportsVM = null;
        this.subscriptions = [];
        this.eventSubscriptions = [];
        this.attendeeIdToEditPopupMap = {};
        this.guidelinesFormSubWindow = {};
        this.configurePassportDisplayFormSubWindow = {};
        this.eventNotes = '';
        this.troubleshootVM = new TroubleshootModalViewModel();
    }

    public handleTroubleshoot = (connection: IConnectionDetailsDTO, openFrom: OpenFrom): void => {
        this.trackingService.get().track(CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS.TROUBLESHOOT_CONNECTION, {
            'Opened From': openFrom
        });
        this.troubleshootVM.setConnection(connection);
    };

    public trackHostJoinClick = (eventName: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, openFrom: OpenFrom): void => {
        this.trackingService.get().track(eventName, {
            'Opened From': openFrom
        });
    };

    public getTroubleshootVM = (): TroubleshootModalViewModel => {
        return this.troubleshootVM;
    };

    public async initialize() {
        await this.fetchEvent();
        this.initRouteListeners();
    }

    public shutdown() {
        this.subscriptions.forEach(x => x());
        this.eventSubscriptions.forEach(x => x?.unsubscribe());
        this.subscriptions = [];
        this.eventSubscriptions = [];
        closeEventNotifications();

        this.reportsVM?.willUnmount();
    }

    @mobx.action
    public async fetchEvent() {
        if (!this.event || this.eventId !== this.event.eventId) {
            this.loadingEvent = true;
            let event;

            try {
                event = await this.eventsDAO.getEventDetails(this.getRequestParams());
            } catch (error) {
                if (error.response.status !== 403) {
                    this.router.navigate(Routes.EventsList, { accountId: this.accountId });
                } else {
                    this.router.navigate(Routes.Restricted, {});
                }
            }

            if (event) {
                this.initEvent(event);
            }
        }
    }

    @mobx.action
    public editEvent = () => {
        if (!this.editEventForm || this.editEventForm.getStatus() === SubWindowStatus.Closed) {
            this.editEventForm = this.subWindowService.showSubWindow({
                title: 'Edit Event',
                componentToHost: EventForm,
                componentToHostProps: {
                    accountId: this.accountId,
                    eventId: this.eventId,
                    saveCompletedCallback: async () => {
                        const response = await this.eventsDAO.getEventDetails(this.getRequestParams());
                        const transpiledEventNotes = transpileInlineStylesToClasses(response.eventNotes);
                        const eventDetails = {...response, eventNotes: transpiledEventNotes };
                        this.setEventDetails(eventDetails);
                    },
                    deleteCompletedCallback: async () =>
                        this.router.navigate(Routes.EventsList, { accountId: this.accountId })
                },
            });
        }
    };

    public getAccountId() {
        return this.accountId;
    }

    public getEvent() {
        return this.event as Readonly<IEventDetailsDTO>;
    }

    public getGuidelinesFormSubWindow = () => {
        return this.guidelinesFormSubWindow;
    };

    public getConfigurePassportDisplayFormSubWindow = () => {
        return this.configurePassportDisplayFormSubWindow;
    };

    public getPage() {
        return this.page;
    }

    public getMeetingsListVM() {
        return this.meetingsListViewModel;
    }

    public getRoomsListVM() {
        return this.roomsViewModel;
    }

    public getConnectionsListVM() {
        return this.connectionsViewModel;
    }

    public getOrganizationsListVM() {
        return this.organizationsListViewModel;
    }

    public getAttendeesListVM() {
        return this.attendeesListViewModel;
    }

    public getEditWebsiteVM() {
        return this.editWebsiteVM;
    }

    public getAssetsVM() {
        return this.assetsVM;
    }

    public getArtifactsVM() {
        return this.artifactsVM;
    }

    public getReportsVM() {
        return this.reportsVM;
    }

    public getControlVM() {
        return this.controlVM;
    }

    public getSalesforceOrderVM() {
        return this.salesforceOrderVM;
    }

    public isLoadingEvent() {
        return this.loadingEvent;
    }

    public getFormattedTimezoneOffset() {
        const offsetInMinutes = getTimezoneOffsetInMinutes(this.event.timeZone);
        return 'GMT' + formatTimezoneOffset(offsetInMinutes);
    }

    public getFullFormattedTimezoneOffset() {
        const { timeZone } = this.event;
        return `(${this.getFormattedTimezoneOffset()}) ${timezonesCodeLabelMap[timeZone] || timeZone}`;
    }

    public getFormattedEventDatesString() {
        return getFormattedDatesString(this.event.startDateTime, this.event.endDateTime, this.event.timeZone, true);
    }

    public getEventTimeZone() {
        return this.event.timeZone;
    }

    private initRouteListeners() {
        this.subscriptions.push(this.router.listenAll(
            [Routes.EventManage, Routes.Meetings],
            () => {
                this.setPage(ManageEventPages.Schedule);
                this.trackingService.get().track(TRACKING_EVENTS.SCHEDULE_TAB_PAGE_LOAD, {});
            }
        ));
        this.subscriptions.push(this.router.listen(
            Routes.OrganizationsList,
            () => {
                this.setPage(ManageEventPages.Organizations);
                this.trackingService.get().track(TRACKING_EVENTS.ORGANIZATIONS_TAB_PAGE_LOAD, {});
            },
        ));
        this.subscriptions.push(this.router.listen(
            Routes.AttendeesList,
            () => {
                this.setPage(ManageEventPages.Attendees);
                this.trackingService.get().track(TRACKING_EVENTS.ATTENDEES_TAB_PAGE_LOAD, {});
            },
        ));
        this.subscriptions.push(this.router.listen(
            Routes.PassportEdit,
            () => {
                this.setPage(ManageEventPages.Website);
            },
        ));
        this.subscriptions.push(this.router.listen(
            Routes.RoomsList,
            () => {
                this.setPage(ManageEventPages.Rooms);
            })
        );
        this.subscriptions.push(this.router.listen(
            Routes.ConnectionsList,
            () => {
                this.setPage(ManageEventPages.Connections);
                this.trackingService.get().track(TRACKING_EVENTS.CONNECTIONS_TAB_PAGE_LOAD, {});
            })
        );
        this.subscriptions.push(this.router.listen(
            Routes.Reports,
            () => {
                this.setPage(ManageEventPages.Reports);
                this.trackingService.get().track(TRACKING_EVENTS.REPORTS_TAB_PAGE_LOAD, {
                    'Account Name': this.globalStorage.getEvent()?.accountName,
                    'Event ID': this.eventId.toString(),
                });
            })
        );
        this.subscriptions.push(this.router.listen(
            Routes.Control,
            () => {
                this.setPage(ManageEventPages.Control);
            })
        );
        this.subscriptions.push(this.router.listen(
            Routes.Assets,
            () => {
                this.setPage(ManageEventPages.Assets);
                this.trackingService.get().track(TRACKING_EVENTS.ASSETS_TAB_PAGE_LOAD, {
                    'Account Name': this.globalStorage.getEvent()?.accountName,
                    'Event ID': this.eventId.toString(),
                    'Event Name': this.globalStorage.getEvent()?.name
                });
            })
        );
        this.subscriptions.push(this.router.listen(
            Routes.Artifacts,
            () => {
                this.setPage(ManageEventPages.Artifacts);
                this.trackingService.get().track(ARTIFACTS_PAGE_TRACKING_EVENTS.ARTIFACTS_TAB_PAGE_LOAD, {});
            })
        );
    }

    public getRequestParams(): IEventRequestParams {
        return {
            accountId: this.accountId,
            eventId: this.eventId,
        };
    }

    public getProperPassportPermission = (): Permission => {
        if (hasPermission(Permission.VIEW_PASSPORT_LINK)) {
            return Permission.VIEW_PASSPORT_LINK;
        } else {
            return Permission.PASSPORT_TAB;
        }
    };

    @mobx.action
    private setEventDetails(eventDetails: IEventDetailsDTO) {
        this.event = eventDetails;
        this.globalStorage.setEvent(eventDetails);
        this.eventNotes = this.event.eventNotes;
        if (!this.loadingEvent) {
            this.roomsViewModel.event = this.event;
            this.meetingsListViewModel.event = this.event;
            this.controlVM.event = this.event;
            this.connectionsViewModel.event = this.event;
            this.reportsVM.event = this.event;
            this.artifactsVM.event = this.event;
            this.salesforceOrderVM.event = this.event;
        }
    }

    public subscribeOnEvent = async (eventId: number) => {
        const subscription = await subscribeAppsyncData<IOnSyncEventSubscriptionResponse>(
            onSyncEvent,
            { eventId: eventId },
            data => {
                /* eslint-disable no-unused-vars */
                const { value: { data: { onSyncEvent: {
                    body,
                    type,
                    dateTime,
                } } } } = data;
                /* eslint-enable no-unused-vars */
            },
            () => this.subscribeOnEvent(eventId)
        );
        this.eventSubscriptions.push(subscription);
    };

    @mobx.action
    private initEvent(eventDetails: IEventDetailsDTO) {
        this.setEventDetails(eventDetails);
        this.initMeetingsPageVM();
        this.initRoomsPageVM();
        this.initConnectionsPageVM();
        this.initAttendeesPageVM();
        this.initOrganizationsPageVM();
        this.initEditWebsiteVM();
        this.initAssetsVM();
        this.initArtifactsVM();
        this.initControlVM();
        this.initSalesforceOrderVM();
        this.initReportsVM();
        this.attendeesListViewModel.setFetchOrganizationsList(this.organizationsListViewModel.fetchOrganizations);
        this.attendeesListViewModel.setFetchMeetingsList(this.meetingsListViewModel.reCallDataList);
        this.attendeesListViewModel.setFetchRoomsWithConnections(this.connectionsViewModel.fetchRoomsWithConnections);
        this.attendeesListViewModel.setAssetsOrganizationsList(this.assetsVM.fetchAssetsOrganizations);
        this.organizationsListViewModel.setFetchMeetingsList(this.meetingsListViewModel.reCallDataList);
        this.organizationsListViewModel.setFetchRoomsWithConnections(this.connectionsViewModel.fetchRoomsWithConnections);
        this.organizationsListViewModel.setFetchRoomsList(this.roomsViewModel.fetchRoomsList);
        this.organizationsListViewModel.setAssetsOrganizationsList(this.assetsVM.fetchAssetsOrganizations);
        this.roomsViewModel.setOperatorsUploadJobCompletedConnectionsCallback(this.connectionsViewModel.fetchRoomsWithConnections);
        this.controlVM.setGenerateMeetingInteractionsReport(this.reportsVM.generateMeetingInteractionsReport);
        this.controlVM.setGenerateMeetingRegistrationReport(this.reportsVM.generateMeetingRegistrationReport);
        this.controlVM.setFetchCustomFields(this.reportsVM.fetchCustomFields);
        this.meetingsListViewModel.setDownloadRegistrantsBySessionReport(() => this.reportsVM.openGenerateRegistrantsBySessionReportModal(true));
        this.meetingsListViewModel.setGenerateMeetingInteractionsReport(this.reportsVM.generateMeetingInteractionsReport);
        this.meetingsListViewModel.setGenerateMeetingRegistrationReport(this.reportsVM.generateMeetingRegistrationReport);
        this.subscribeOnEvent(eventDetails.eventId);
        this.loadingEvent = false;
    }

    @mobx.action
    private initMeetingsPageVM = () => {
        this.meetingsListViewModel = new MeetingsListViewModel(this.accountId, this.intl, this.subWindowFormMethods);
        this.meetingsListViewModel.event = this.event;
        this.meetingsListViewModel.showEventNotesPopup = this.showEventNotesPopup.bind(this);
        this.meetingsListViewModel.guidelinesFormSubWindow = this.guidelinesFormSubWindow;
        this.meetingsListViewModel.showAgentMemoryPopup = this.showAgentMemoryPopup.bind(this);

        this.meetingsListViewModel.initialize();
        this.subWindowFormMethods.schedule = { openEditSubWindow: this.meetingsListViewModel.handleEditClick };
        this.meetingsListViewModel.initAttendeDetailsPopoverVM();
    };

    @mobx.action
    private initRoomsPageVM = () => {
        this.roomsViewModel = new RoomsViewModel(this.accountId, this.initMeetingsPageVM, this.intl);
        this.roomsViewModel.event = this.event;
        this.roomsViewModel.setHandleTroubleshoot(this.handleTroubleshoot);
        this.roomsViewModel.setShowConfigurePassport(this.showConfigurePassportForm);
        this.roomsViewModel.setTrackHostJoinClick(this.trackHostJoinClick);
        this.roomsViewModel.initialize();
        this.subWindowFormMethods.rooms = { openEditSubWindow: this.roomsViewModel.updateRoom };
    };

    @mobx.action
    private initConnectionsPageVM = () => {
        this.connectionsViewModel = new ConnectionsViewModel(this.accountId, this.event, this.intl, this.subWindowFormMethods);
        this.connectionsViewModel.initZoomMeetingVM();
        this.connectionsViewModel.initWebinarMeetingVM();
        this.connectionsViewModel.showEventNotesPopup = this.showEventNotesPopup.bind(this);
        this.connectionsViewModel.guidelinesFormSubWindow = this.guidelinesFormSubWindow;
        this.connectionsViewModel.showConfigurePassportForm = this.showConfigurePassportForm.bind(this);

        this.subWindowFormMethods.meetingConnections = {
            openEditSubWindow: this.connectionsViewModel.updateConnections
        };
        this.subWindowFormMethods.meetingConnectionDetails = {
            openEditSubWindow: this.connectionsViewModel.editMeetingConnection,
            openDeleteSubWindow: this.connectionsViewModel.deleteMeetingConnection,
        };
        this.subWindowFormMethods.roomConnectionDetails = {
            openEditSubWindow: this.connectionsViewModel.editRoomConnection,
            openDeleteSubWindow: this.connectionsViewModel.deleteRoomConnection,
        };
        this.connectionsViewModel.setHandleTroubleshoot(this.handleTroubleshoot);
        this.connectionsViewModel.setTrackHostJoinClick(this.trackHostJoinClick);
    };

    @mobx.action
    private initOrganizationsPageVM = () => {
        this.organizationsListViewModel = new OrganizationsListViewModel(
            this.accountId,
            this.eventId,
            this.intl,
            this.attendeesListViewModel.fetchAttendees,
            this.attendeeIdToEditPopupMap,
        );
        this.organizationsListViewModel.initialize();

    };

    @mobx.action
    private initAttendeesPageVM = () => {
        this.attendeesListViewModel = new AttendeeListViewModel(
            this.accountId,
            this.eventId,
            this.attendeeIdToEditPopupMap,
            this.intl,
        );
        this.attendeesListViewModel.initialize();
        this.subWindowFormMethods.attendees = {
            openEditSubWindow: this.attendeesListViewModel.editAttendee,
            openNewSubWindow: this.attendeesListViewModel.addAttendee
        };
    };

    @mobx.action
    private initEditWebsiteVM = () => {
        this.editWebsiteVM = new EditWebsiteViewModel(this.accountId, this.event, this.intl);
    };

    @mobx.action
    private initAssetsVM = () => {
        this.assetsVM = new AssetsViewModel(this.accountId, this.event.eventId, this.intl);
    };

    @mobx.action
    private initArtifactsVM = () => {
        this.artifactsVM = new ArtifactsViewModel(this.event, this.intl);
    };

    @mobx.action
    private initSalesforceOrderVM = () => {
        this.salesforceOrderVM = new SalesforceOrderViewModel(this.event, this.accountId);
    };

    @mobx.action
    private initReportsVM = () => {
        this.reportsVM = new ReportsViewModel(this.accountId, this.event);
    };

    @mobx.action
    private initControlVM = () => {
        this.controlVM = new ControlViewModel(this.accountId, this.event, this.intl, this.subWindowFormMethods);
        this.controlVM.initZoomMeetingVM();
        this.controlVM.initWebinarMeetingVM();
        this.controlVM.setHandleTroubleshoot(this.handleTroubleshoot);
        this.controlVM.setTrackHostJoinClick(this.trackHostJoinClick);
        this.controlVM.setConnectionVMInstance(this.connectionsViewModel);
        this.controlVM.showEventNotesPopup = this.showEventNotesPopup.bind(this);
        this.controlVM.guidelinesFormSubWindow = this.guidelinesFormSubWindow;
        this.controlVM.showConfigurePassportForm = this.showConfigurePassportForm.bind(this);
        this.subWindowFormMethods.control = { openEditSubWindow: this.controlVM.handleRunClick };
    };

    @mobx.action
    private setPage(page: ManageEventPages) {
        this.page = page;
    }

    @mobx.action
    public async handleEventNotesChange(eventNotes: string, openFrom: OpenFrom): Promise<boolean> {
        try {
            const eventNotesValue = transpileClassesToInlineStyles(eventNotes);
            await this.eventsDAO.updateEventGuidlines({
                accountId: this.accountId,
                eventId: this.event.eventId,
                eventNotes: eventNotesValue
            });
            mobx.runInAction(() => {
                this.event.eventNotes = eventNotes;
                this.eventNotes = eventNotes;
            });
            this.trackGuidelinesMixPanel(openFrom, true);
            return true;
        } catch (e) {
            alertServerError(this.intl.formatMessage({
                id: 'oe.common.error',
                defaultMessage: 'Error',
                description: 'Label text',
            }), this.errorParsingService.extractMessage(e));
            return false;
        }
    }

    @mobx.action
    public setEventNotes = (value: string): void => {
        this.eventNotes = value;
    };

    public getEventNotes = (): string => {
        return this.eventNotes;
    };

    @mobx.action
    public showEventNotesPopup = (isEditMode: boolean = false, openFrom: OpenFrom) => {
        const popup = isEditMode ? GuidelinesMode.Edit : GuidelinesMode.Create;
        this.setEventNotes(this.event.eventNotes);

        const existingSubWindow = this.guidelinesFormSubWindow?.[popup] || null;
        if (existingSubWindow && existingSubWindow?.getStatus() !== SubWindowStatus.Closed) {
            // if we successfully maximized existing one
            existingSubWindow.maximize();
        }

        if (!this.guidelinesFormSubWindow[popup] || this.guidelinesFormSubWindow[popup].getStatus() === SubWindowStatus.Closed) {
            this.guidelinesFormSubWindow[popup] = this.subWindowService.showSubWindow({
                title: isEditMode ?
                    this.intl.formatMessage({
                        id: 'oe.control.editGuidelines',
                        defaultMessage: 'Edit Guidelines',
                        description: 'Edit general guidelines'
                    }) :
                    this.intl.formatMessage({
                        id: 'oe.control.viewGuidelines',
                        defaultMessage: 'View Guidelines',
                        description: 'View general guidelines'
                    }),
                withExpand: true,
                className: 'oe-popup-layout--positioned',
                componentToHost: GuidelinesForm,
                componentToHostProps: {
                    intl: this.intl,
                    handleEventNotesChange: (
                        eventNotes: string,
                        // eslint-disable-next-line @typescript-eslint/no-shadow
                        openFrom: OpenFrom
                    ) => this.handleEventNotesChange(eventNotes, openFrom),
                    isEditMode: isEditMode,
                    openFrom: openFrom,
                    setEventNotes: this.setEventNotes,
                    getEventNotes: this.getEventNotes,
                    trackGuidelinesMixPanel: this.trackGuidelinesMixPanel,
                    fetchEventNotes: this.fetchEventNotes
                },
                onCloseIconClickHandler: () => {
                    this.guidelinesFormSubWindow[popup].close();
                    if (isEditMode) this.trackGuidelinesMixPanel(openFrom, false);
                    return true;
                }
            });
        }
    };

    @mobx.action
    public showAgentMemoryPopup = (openFrom: OpenFrom) => {
        if (this.agentMemoryFormSubWindow && this.agentMemoryFormSubWindow.getStatus() !== SubWindowStatus.Closed) {
            this.agentMemoryFormSubWindow.maximize();
            return;
        }
        this.agentMemoryFormSubWindow = this.subWindowService.showSubWindow({
            title: this.intl.formatMessage(EDIT_AGENT_MEMORY_POPUP_TITLE_TEXT),
            withExpand: true,
            className: 'oe-popup-layout--positioned',
            componentToHost: AgentMemoryForm,
            componentToHostProps: {
                intl: this.intl,
                fetchAgentMemory: this.fetchAgentMemory,
                saveAgentMemory: (agentMemory: string) => this.saveAgentMemory(agentMemory, openFrom),
            },
            onCloseIconClickHandler: () => {
                this.agentMemoryFormSubWindow.close();
                return true;
            }
        });
    };

    public fetchAgentMemory = async (): Promise<IAgentMemoryDTO | null> => {
        try {
            const response = await this.eventsDAO.getAgentMemory(this.getRequestParams());
            this.loadedAgentMemory = response.agentMemory ?? '';
            return response;
        } catch (error) {
            alertServerError(this.intl.formatMessage(ERROR_TEXT), this.errorParsingService.extractMessage(error));
            return null;
        }
    };

    public saveAgentMemory = async (agentMemory: string, openFrom: OpenFrom): Promise<IAgentMemoryDTO | null> => {
        try {
            const response = await this.eventsDAO.updateAgentMemory({ ...this.getRequestParams(), agentMemory: agentMemory });
            // the backend skips unchanged content, so tracking mirrors that
            if (agentMemory !== this.loadedAgentMemory) {
                this.trackAgentMemoryMixPanel(openFrom, agentMemory);
            }
            this.loadedAgentMemory = response.agentMemory ?? '';
            return response;
        } catch (error) {
            alertServerError(this.intl.formatMessage(ERROR_TEXT), this.errorParsingService.extractMessage(error));
            return null;
        }
    };

    // never send the memory text itself, it is client data
    private trackAgentMemoryMixPanel = (openFrom: OpenFrom, agentMemory: string): void => {
        this.trackingService.get().track(TRACKING_EVENTS.AGENT_MEMORY_UPDATED, {
            'Open From': openFrom,
            'Account ID': this.accountId,
            'Event ID': this.eventId,
            'Content length': agentMemory.length,
        });
    };

    @mobx.action
    public showConfigurePassportForm = (props: IConfigurePassportDisplayFormProps, connection: IConnectionDetailsDTO) => {
        const popup = props.meetingConnectionId;
        const existingSubWindow = this.configurePassportDisplayFormSubWindow?.[popup] || null;
        if (existingSubWindow && existingSubWindow?.getStatus() !== SubWindowStatus.Closed) {
            // if we successfully maximized existing one
            existingSubWindow.maximize();
        }

        if (!this.configurePassportDisplayFormSubWindow[popup] || this.configurePassportDisplayFormSubWindow[popup].getStatus() === SubWindowStatus.Closed) {
            this.configurePassportDisplayFormSubWindow[popup] = this.subWindowService.showSubWindow({
                title: this.intl.formatMessage(CONFIGURE_PASSPORT_DISPLAY_TEXT),
                className: 'oe-popup-layout--positioned',
                componentToHost: ConfigurePassportDisplayForm,
                componentToHostProps: {
                    props: props,
                    connection: connection,
                    passportConfigurationMode: PassportConfigurationMode.MeetingConnection
                },
                onCloseIconClickHandler: () => {
                    this.configurePassportDisplayFormSubWindow[popup].close();
                    return true;
                }
            });
        }
    };


    public trackGuidelinesMixPanel = (
        openFrom: OpenFrom,
        dataSaved: boolean
    ): void => {
        const trackData = {
            'Open From': openFrom,
            'Data entered': convertBoolTypeForMixPanel(this.getEventNotes() !== this.event.eventNotes),
            'Data saved': convertBoolTypeForMixPanel(dataSaved)
        };
        this.trackingService.get().track(TRACKING_EVENTS.EDIT_GUIDELINES, trackData);
    };


    public fetchEventNotes = async (): Promise<void> => {
        try {
            const response = await this.eventsDAO.getEventDetails(this.getRequestParams());
            mobx.runInAction(() => {
                this.setEventNotes(transpileInlineStylesToClasses(response.eventNotes));
            });
        } catch (error) {
            alertServerError(this.intl.formatMessage(ERROR_TEXT), this.errorParsingService.extractMessage(error));
        }
    };

}

export { ManageEventViewModel, ManageEventPages, IEditMeetingConnectionDetailsProps };
